import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../services/auth.service';

import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { InputOtpModule } from '@openng/optimus-ui/inputotp';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ErrorBannerComponent } from '../../shared/error-banner.component';

/** The second step of a sign-in for an operator with 2FA, as in the app's verify-two-factor. */
@Component({
  selector: 'app-verify-two-factor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
    CardModule,
    InputOtpModule,
    InputTextModule,
    ErrorBannerComponent,
  ],
  templateUrl: './verify-two-factor.component.html',
  styleUrl: '../login.component.scss',
})
export class VerifyTwoFactorComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  protected loading = signal(false);
  protected useRecoveryCode = signal(false);
  protected verifyError = signal<string | null>(null);

  protected verifyForm = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(6)],
    }),
    recoveryCode: new FormControl('', { nonNullable: true }),
  });

  constructor() {
    // The Google callback hands the challenge over in the fragment; keep it, then drop it from the
    // address bar so it is not left in history.
    const fragment = inject(ActivatedRoute).snapshot.fragment;
    if (fragment?.startsWith('mfa=')) {
      this.authService.setMfaToken(fragment.slice('mfa='.length));
      const location = inject(Location);
      location.replaceState(location.path());
    }
  }

  protected toggleMethod(): void {
    const isRecovery = !this.useRecoveryCode();
    this.useRecoveryCode.set(isRecovery);
    this.verifyError.set(null);

    const { code, recoveryCode } = this.verifyForm.controls;
    code.reset();
    recoveryCode.reset();
    code.setValidators(isRecovery ? [] : [Validators.required, Validators.minLength(6)]);
    recoveryCode.setValidators(isRecovery ? [Validators.required, Validators.minLength(10)] : []);
    code.updateValueAndValidity();
    recoveryCode.updateValueAndValidity();
  }

  protected onSubmit(): void {
    if (this.loading() || this.verifyForm.invalid) return;

    const mfaToken = this.authService.getMfaToken();
    if (!mfaToken) {
      this.router.navigate(['/login']);
      return;
    }

    this.loading.set(true);
    this.verifyError.set(null);

    const isRecovery = this.useRecoveryCode();
    const { code, recoveryCode } = this.verifyForm.getRawValue();

    this.authService
      .verifyLogin2fa(mfaToken, isRecovery ? recoveryCode : code, isRecovery ? 'recovery' : 'totp')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loading.set(false),
        error: (err) => {
          this.loading.set(false);
          this.verifyError.set(
            err.status === 429
              ? 'Too many attempts. Try again in a minute.'
              : (err.error?.message ?? 'Invalid code'),
          );
          this.verifyForm.reset();
        },
      });
  }
}
